import React, { useMemo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withLocalize } from 'react-localize-redux';
import { useFormikContext } from 'formik-2';
import { useSelector } from 'react-redux';
import Table from '../../../components/organisms/Table';
import TableCell from '../../../components/widgets/TableCell';
import Select from '../../../components/widgets/Select';
import {
    ATTRIBUTION_TAB_IDS,
    TRANSLATE_PREFIX,
    URL_QUERY_KEY_REGEX,
    buildPassthroughAdnetworkMacro,
    getDefaultPostbackMacros,
    getPostbackSingularMacros,
    isTrackingKeyConfigured,
    trackingLinkKeyFromMacro,
} from '../../consts';
import DropdownOptionLabel from '../DropdownOptionLabel';
import SingularMacroCell from './SingularMacroCell';
import ParameterKeyCell from './ParameterKeyCell';
import { macroDescriptionKey, macroForTranslationKeys, macroLabelKey } from './consts';
import { getPostbackMacroNetworkName } from '../../../selectors/partnerIntegration';
import { useTabUpdater } from '../../formUpdaters';
import sharedTableCss from '../formTable.css';
import css from './PostbackTemplateMacroTable.css';

const TAB_PREFIX = `${TRANSLATE_PREFIX}.POSTBACK_TEMPLATES_TAB`;
const TAB_ID = ATTRIBUTION_TAB_IDS.POSTBACK_TEMPLATES;

const PostbackTemplateMacroTable = ({ eventType, platform, readOnly = false, translate }) => {
    const { values } = useFormikContext();
    const updateTab = useTabUpdater(TAB_ID);
    const postbackMacroNetworkName = useSelector(getPostbackMacroNetworkName);
    const postbackSingularMacros = useMemo(() => getPostbackSingularMacros(postbackMacroNetworkName, platform), [
        postbackMacroNetworkName,
        platform,
    ]);
    const postbackSingularMacroSet = useMemo(() => new Set(postbackSingularMacros), [postbackSingularMacros]);
    const savedRows = useMemo(() => values.formData?.[TAB_ID]?.[eventType]?.[platform] || [], [
        values.formData,
        eventType,
        platform,
    ]);

    const trackingLinkParameters = useMemo(
        () => values.formData?.[ATTRIBUTION_TAB_IDS.TRACKING_LINK_OPTIONS]?.tracking_link_parameters || [],
        [values.formData]
    );

    const defaultsForCell = useMemo(() => getDefaultPostbackMacros(platform, eventType, postbackMacroNetworkName), [
        platform,
        eventType,
        postbackMacroNetworkName,
    ]);

    // Mutators read prev rows via setValues(prev => ...) so concurrent edits to the same
    // event/platform table can't overwrite each other through a stale closure.
    const updateRows = updater =>
        updateTab(prevTab => {
            const prevEvent = prevTab[eventType] || {};
            const prevRows = prevEvent[platform] || [];
            return {
                ...prevTab,
                [eventType]: {
                    ...prevEvent,
                    [platform]: updater(prevRows),
                },
            };
        });

    const updateKey = (macro, key) => {
        updateRows(prevRows => {
            const idx = prevRows.findIndex(r => r.singular_macro === macro);
            const next = prevRows.slice();
            if (idx >= 0) {
                next[idx] = { ...next[idx], partner_parameter_key: key };
            } else {
                next.push({ singular_macro: macro, partner_parameter_key: key });
            }
            return next;
        });
    };

    const removeMacro = macro => updateRows(prevRows => prevRows.filter(r => r.singular_macro !== macro));

    const addMacro = macro => {
        updateRows(prevRows => [...prevRows, { singular_macro: macro, partner_parameter_key: '' }]);
    };

    const displayRows = useMemo(() => {
        const buildRow = row => {
            const passthroughKey = trackingLinkKeyFromMacro(row.singular_macro);
            return {
                singular_macro: row.singular_macro,
                partner_parameter_key: row.partner_parameter_key || '',
                isDefault: defaultsForCell.includes(row.singular_macro),
                requiresTrackingLinkParam:
                    !!passthroughKey && !isTrackingKeyConfigured(passthroughKey, trackingLinkParameters),
            };
        };
        return savedRows.map(buildRow);
    }, [savedRows, trackingLinkParameters, defaultsForCell]);

    const usedMacros = new Set(displayRows.map(r => r.singular_macro));
    const customMacros = [
        ...new Set(
            trackingLinkParameters
                .filter(p => p.is_custom && !!p.singular_key?.trim())
                .map(p => buildPassthroughAdnetworkMacro(p.singular_key.trim(), postbackMacroNetworkName))
        ),
    ];
    const customMacroOptions = customMacros
        .filter(m => !usedMacros.has(m) && !postbackSingularMacroSet.has(m))
        .map(m => ({
            value: m,
            label: translate(`${TAB_PREFIX}.MACRO_LABELS.CUSTOM_TRACKING_LINK_MACRO`),
        }));
    const standardOptions = postbackSingularMacros
        .filter(m => !usedMacros.has(m))
        .map(m => ({
            value: m,
            label: translate(macroLabelKey(macroForTranslationKeys(m, postbackMacroNetworkName))),
        }));
    const divider = { value: '__divider__', label: '', isDivider: true, isDisabled: true };
    const dropdownOptions =
        customMacroOptions.length && standardOptions.length
            ? [...customMacroOptions, divider, ...standardOptions]
            : [...customMacroOptions, ...standardOptions];

    const tableMetadata = [
        {
            name: 'singular_macro',
            displayName: `${TAB_PREFIX}.PARAMETER_TABLE.SINGULAR_MACRO`,
            cellComponent: SingularMacroCell,
            cellProps: { onRemove: removeMacro, readOnly, translate },
        },
        {
            name: 'description',
            displayName: `${TAB_PREFIX}.PARAMETER_TABLE.DESCRIPTION`,
            cellComponent: TableCell,
            headerWidth: '50%',
            cellProps: { textClassName: css.descriptionCell },
        },
        {
            name: 'parameter_key',
            displayName: `${TAB_PREFIX}.PARAMETER_TABLE.TRACKING_LINK_PARAMETER_KEY`,
            cellComponent: ParameterKeyCell,
            cellProps: { onChange: updateKey, readOnly, translate },
        },
    ];

    const describeMacro = macro => {
        if (postbackSingularMacroSet.has(macro)) {
            return translate(macroDescriptionKey(macroForTranslationKeys(macro, postbackMacroNetworkName))) || '';
        }
        const tk = trackingLinkKeyFromMacro(macro);
        if (tk) {
            const param = trackingLinkParameters.find(p => p.singular_key === tk);
            return param?.description || '';
        }
        return '';
    };

    const partnerKeyCounts = displayRows.reduce((acc, r) => {
        const k = r.partner_parameter_key?.trim();
        if (k) acc[k] = (acc[k] || 0) + 1;
        return acc;
    }, {});

    const tableData = displayRows.map(row => {
        const partnerKey = row.partner_parameter_key || '';
        const paramErrorKey = !URL_QUERY_KEY_REGEX.test(partnerKey)
            ? 'POSTBACK_TEMPLATE_PARAM_KEY_INVALID_CHARS'
            : null;
        const trimmedKey = partnerKey.trim();
        const duplicateKey = !readOnly && !!trimmedKey && partnerKeyCounts[trimmedKey] > 1;
        return {
            key: row.singular_macro,
            singular_macro: row.singular_macro,
            description: describeMacro(row.singular_macro),
            partner_parameter_key: row.partner_parameter_key,
            isDefault: row.isDefault,
            requiresTrackingLinkParam: row.requiresTrackingLinkParam,
            paramErrorKey,
            duplicateKey,
        };
    });

    return (
        <div className={classNames(css.tableWrapper)}>
            <Table
                metadata={tableMetadata}
                data={tableData}
                zebraTable={false}
                rowHoverEnabled={false}
                showInnerBorder={false}
                tableClass={sharedTableCss.table}
                theadClass={sharedTableCss.thead}
                thClass={sharedTableCss.th}
                trClass={sharedTableCss.row}
            />
            {!readOnly && (
                <div className={css.addRow}>
                    <Select
                        options={dropdownOptions}
                        value={null}
                        onChange={opt => opt && addMacro(opt.value)}
                        placeholder={translate(`${TAB_PREFIX}.ADD_MACRO_PLACEHOLDER`)}
                        isSearchable
                        isClearable={false}
                        isDisabled={!dropdownOptions.length}
                        className={css.addMacroSelect}
                        formatOptionLabel={DropdownOptionLabel}
                        classNames={{
                            option: ({ data }) => (data?.isDivider ? css.dividerOption : ''),
                        }}
                        usePortal
                    />
                </div>
            )}
        </div>
    );
};

PostbackTemplateMacroTable.propTypes = {
    eventType: PropTypes.string.isRequired,
    platform: PropTypes.string.isRequired,
    readOnly: PropTypes.bool,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(PostbackTemplateMacroTable);
